import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Send, MessageSquare } from 'lucide-react';
import { messagesApi } from '@/api/messages';
import { Loading, EmptyState } from '@/components/ui';
import { cn } from '@/utils/helpers';
import { formatRelative } from '@/utils/date';
import type { OrgMessage } from '@/types';
import toast from 'react-hot-toast';

// ── Message Bubble ────────────────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: OrgMessage }) {
  const isOut = msg.direction === 'outbound';
  return (
    <div className={cn('flex', isOut ? 'justify-start' : 'justify-end')}>
      <div className={cn(
        'max-w-[70%] rounded-2xl px-4 py-2.5 text-sm',
        isOut
          ? 'bg-navy-card border border-navy-border text-slate-200 rounded-bl-sm'
          : 'bg-teal/20 border border-teal/30 text-slate-100 rounded-br-sm'
      )}>
        <p className="leading-relaxed">{msg.content}</p>
        <p className={cn('text-[10px] mt-1', isOut ? 'text-slate-500' : 'text-teal/70')}>
          {formatRelative(msg.sent_at ?? '')}
        </p>
      </div>
    </div>
  );
}

// ── Quick Send Bar ────────────────────────────────────────────────────────────

function SendBar({ orgId }: { orgId: string }) {
  const [to, setTo] = useState('');
  const [content, setContent] = useState('');

  const { data: accounts } = useQuery({
    queryKey: ['whatsapp_accounts_admin', orgId],
    queryFn: () => messagesApi.getWhatsappAccounts(orgId),
  });

  const [accountId, setAccountId] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      messagesApi.send({
        org_id: orgId,
        whatsapp_account_id: accountId,
        to_address: to,
        content,
        channel: 'whatsapp',
      }),
    onSuccess: () => { toast.success('הודעה נשלחה'); setTo(''); setContent(''); },
    onError: (e: Error) => toast.error(`שגיאת שליחה: ${e.message}`),
  });

  return (
    <div className="bg-navy-card border border-navy-border rounded-xl p-4" dir="rtl">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Send className="h-4 w-4 text-gold" />
        שלח הודעה
      </h3>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-slate-400 mb-1">מספר נמען</label>
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="0521234567"
            dir="ltr" className="w-full h-8 px-3 rounded-lg bg-navy-dark border border-navy-border text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-gold/30" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">חשבון שולח</label>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
            className="w-full h-8 px-3 rounded-lg bg-navy-dark border border-navy-border text-sm text-white focus:outline-none focus:ring-1 focus:ring-gold/30">
            <option value="">בחר חשבון...</option>
            {accounts?.map((a: { id: string; name: string; phone_number: string | null }) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.phone_number ?? '—'})
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">תוכן הודעה</label>
        <input value={content} onChange={(e) => setContent(e.target.value)} placeholder="כתוב הודעה..."
          dir="rtl" className="w-full h-8 px-3 rounded-lg bg-navy-dark border border-navy-border text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-gold/30" />
      </div>
      <div className="flex justify-end mt-3">
        <button
          onClick={() => mutation.mutate()}
          disabled={!to || !content || !accountId || mutation.isPending}
          className="flex items-center gap-1.5 h-8 px-4 bg-gold text-navy-dark text-sm font-bold rounded-lg hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="h-3.5 w-3.5" />
          {mutation.isPending ? 'שולח...' : 'שלח'}
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function OrgMessagesTab({ orgId }: { orgId: string }) {
  const { data: messages, isLoading } = useQuery({
    queryKey: ['org_messages_admin', orgId],
    queryFn: () => messagesApi.listForOrg(orgId),
  });

  return (
    <div dir="rtl" className="space-y-5">
      {/* Send bar */}
      <SendBar orgId={orgId} />

      {/* Messages list */}
      <div className="bg-navy-card border border-navy-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-navy-border flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-teal" />
          <h3 className="text-sm font-semibold text-white">היסטוריית הודעות</h3>
          {messages && <span className="ms-auto text-xs text-slate-500">{messages.length} הודעות</span>}
        </div>

        {isLoading ? (
          <div className="p-6"><Loading text="טוען הודעות..." /></div>
        ) : !messages?.length ? (
          <div className="p-8">
            <EmptyState
              icon={<MessageSquare className="h-10 w-10 text-slate-600" />}
              title="אין הודעות עדיין"
              description="הודעות שנשלחות ומתקבלות יופיעו כאן"
            />
          </div>
        ) : (
          <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
            {messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
          </div>
        )}
      </div>
    </div>
  );
}
